// ChatsPage — the dedicated "all chats" destination. Searchable table; row click opens (resumes) the
// chat; per-row ⋮ menu (Rename · Move to project [coming soon] · Copy session ID · Delete chat).
// Studio Cyan/Stone tokens.
//
// Delete UX: ⋮ → Delete opens a styled confirm dialog; on confirm the row is removed from the list
// immediately and a 5s Undo Snackbar shows, with the SERVER delete DEFERRED until the toast window
// expires. Undo restores the row locally and cancels the deferred commit, so nothing is lost.

import { useEffect, useMemo, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Snackbar from '@mui/material/Snackbar'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Divider from '@mui/material/Divider'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Paper from '@mui/material/Paper'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined'
import PushPinIcon from '@mui/icons-material/PushPin'
import CheckIcon from '@mui/icons-material/Check'
import type { SessionSummary } from '@reachinsight-app/shared/src/components/R3Chat/types'
import { useRosShell } from '../RosShellContext'
import { ArtifactIndicator } from '../components/ArtifactIndicator'
import { useNavigate } from 'react-router-dom'
import { listProjects, createProject, addChatToProject, removeChatFromProject, type ProjectListItem } from './projectsApi'
import { SERIF_HEADING } from '../theme'

const UNDO_MS = 5000

function relativeTime(startedAt: number): string {
  const now = Date.now()
  const days = Math.floor((now - startedAt) / 86_400_000)
  if (days <= 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 7) return `${days} days ago`
  if (days < 14) return 'Last week'
  if (days < 31) return `${Math.floor(days / 7)} weeks ago`
  return new Date(startedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

export function ChatsPage() {
  const shell = useRosShell()
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [menu, setMenu] = useState<{ anchor: HTMLElement; session: SessionSummary } | null>(null)
  // "Move to project" submenu: anchored to the same ⋮ position, listing projects + Unassigned + New.
  const [projectMenu, setProjectMenu] = useState<{ anchor: HTMLElement; session: SessionSummary } | null>(null)
  const [projects, setProjects] = useState<ProjectListItem[]>([])
  const [confirmDelete, setConfirmDelete] = useState<SessionSummary | null>(null)
  const [pendingDelete, setPendingDelete] = useState<SessionSummary | null>(null)
  const commitTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // The pending row lives in a ref so the unmount-flush below can read it WITHOUT depending on
  // `pendingDelete` — depending on it would re-run the effect's cleanup on every change, firing the
  // server delete the instant the user clicks Undo (which sets pendingDelete back to null).
  const pendingRef = useRef<SessionSummary | null>(null)
  useEffect(() => { pendingRef.current = pendingDelete }, [pendingDelete])
  const suppressBlurRef = useRef(false)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    const visible = shell.sessions.filter(s => s.id !== pendingDelete?.id)
    if (!q) return visible
    return visible.filter(s => `${s.intentSummary || ''} ${s.projectName || ''}`.toLowerCase().includes(q))
  }, [shell.sessions, query, pendingDelete])

  // Commit a still-pending delete only on REAL unmount (empty deps; reads the ref, not state — so the
  // cleanup never fires on the Undo transition).
  useEffect(() => () => {
    if (commitTimer.current) clearTimeout(commitTimer.current)
    if (pendingRef.current) shell.deleteSession(pendingRef.current.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const startRename = (s: SessionSummary) => {
    setEditValue(s.intentSummary || '')
    suppressBlurRef.current = true  // ignore the blur the menu-close transition triggers on mount
    setEditingId(s.id)
    setMenu(null)  // enter edit mode BEFORE closing the menu so the field mounts cleanly
  }
  const commitRename = () => {
    if (!editingId) return
    const name = editValue.trim()
    if (name) shell.renameSession(editingId, name)
    setEditingId(null)
  }
  const onFieldBlur = () => {
    // The menu closing on rename-open steals focus from the autoFocus field, firing a spurious blur
    // that would instantly exit rename mode. Swallow that first blur; honor real ones (and Enter) after.
    if (suppressBlurRef.current) { suppressBlurRef.current = false; return }
    commitRename()
  }

  const confirmAndDelete = () => {
    const s = confirmDelete
    setConfirmDelete(null)
    if (!s) return
    // Flush any prior pending delete first (one Undo at a time).
    if (pendingDelete) { if (commitTimer.current) clearTimeout(commitTimer.current); shell.deleteSession(pendingDelete.id) }
    setPendingDelete(s)
    commitTimer.current = setTimeout(() => { shell.deleteSession(s.id); setPendingDelete(null) }, UNDO_MS)
  }
  const undoDelete = () => {
    if (commitTimer.current) clearTimeout(commitTimer.current)
    if (pendingDelete) shell.restoreSessionLocally(pendingDelete)
    setPendingDelete(null)
  }

  const copySessionId = (id: string) => { setMenu(null); navigator.clipboard?.writeText(id).catch(() => {}) }

  // "Move to project": assignment is atomic on the server; selecting another project never leaves
  // an intermediate duplicate membership.
  // Anchor to the ⋮ button (stays in the DOM), NOT the clicked menu item — that item unmounts when
  // the first menu closes, which would detach the submenu's anchor and prevent it from opening.
  const openProjectMenu = async (anchor: HTMLElement, session: SessionSummary) => {
    setMenu(null)
    setProjectMenu({ anchor, session })
    try { setProjects(await listProjects(shell.apiBaseUrl, shell.token)) } catch { /* empty list */ }
  }
  const moveToProject = async (projectId: string) => {
    const sess = projectMenu?.session
    setProjectMenu(null)
    if (!sess) return
    if (sess.projectId === projectId) return
    try {
      await addChatToProject(shell.apiBaseUrl, shell.token, projectId, sess.id)
      shell.reloadSessions()
    } catch { /* ignore */ }
  }
  const moveOutOfProject = async () => {
    const sess = projectMenu?.session
    setProjectMenu(null)
    if (!sess?.projectId) return
    try {
      await removeChatFromProject(shell.apiBaseUrl, shell.token, sess.projectId, sess.id)
      shell.reloadSessions()
    } catch { /* ignore */ }
  }
  const createProjectWithChat = async () => {
    const sess = projectMenu?.session
    setProjectMenu(null)
    if (!sess) return
    try {
      const id = await createProject(shell.apiBaseUrl, shell.token, 'Untitled project')
      await addChatToProject(shell.apiBaseUrl, shell.token, id, sess.id)
      shell.reloadSessions()
      navigate(`/projects/${id}`)
    } catch { /* ignore */ }
  }

  return (
    <Box sx={{ maxWidth: 980, mx: 'auto', px: 4, py: 5 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h3" sx={SERIF_HEADING}>Chats</Typography>
          <Typography sx={{ color: 'text.secondary', mt: 0.5, maxWidth: 640 }}>
            Every conversation you've had with Ros. Open one to pick up exactly where you left off.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={shell.newChat} sx={{ flexShrink: 0, mt: 0.5 }}>New chat</Button>
      </Box>

      {/* Search + count */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
        <TextField
          size="small" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search chats"
          sx={{ width: 300 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <Typography sx={{ ml: 'auto', color: 'text.secondary', fontSize: '0.8rem' }}>
          {filtered.length} {filtered.length === 1 ? 'chat' : 'chats'}
        </Typography>
      </Box>

      {/* Table */}
      <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
        {/* header row */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2.5, py: 1, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ flex: 1, fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary' }}>Chat</Typography>
          <Typography sx={{ width: 190, fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary' }}>Project</Typography>
          <Typography sx={{ width: 90, fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary' }}>Artifacts</Typography>
          <Typography sx={{ width: 140, fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary' }}>Last active</Typography>
          <Box sx={{ width: 40 }} />
        </Box>

        {filtered.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Typography sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
              {query ? `No chats match “${query}”` : 'No chats yet'}
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
              {query ? 'Try a different search.' : 'Start one from the Home page.'}
            </Typography>
          </Box>
        ) : filtered.map((s, i) => (
          <Box
            key={s.id}
            onClick={() => { if (editingId !== s.id) shell.selectSession(s.id) }}
            sx={{
              display: 'flex', alignItems: 'center', px: 2.5, py: 1.4, cursor: editingId === s.id ? 'default' : 'pointer',
              borderTop: i === 0 ? 'none' : '1px solid', borderColor: 'divider',
              '&:hover': { bgcolor: 'action.hover' }, '&:hover .row-more': { opacity: 1 },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0, pr: 2 }}>
              {editingId === s.id ? (
                <TextField
                  autoFocus size="small" value={editValue}
                  onClick={e => e.stopPropagation()}
                  onChange={e => setEditValue(e.target.value)}
                  onBlur={onFieldBlur}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); commitRename() }
                    else if (e.key === 'Escape') { suppressBlurRef.current = false; setEditingId(null) }
                  }}
                  sx={{
                    minWidth: 320,
                    '& .MuiOutlinedInput-root': { borderRadius: 999, fontWeight: 600, fontSize: '0.9rem' },
                    '& .MuiOutlinedInput-input': { py: 0.6 },
                  }}
                />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: '0.9rem', fontWeight: 500 }}>{s.intentSummary || 'Untitled chat'}</Typography>
                </Box>
              )}
            </Box>
            <Box sx={{ width: 190, minWidth: 0, pr: 2, display: 'flex', alignItems: 'center', gap: 0.6, color: s.projectName ? 'text.secondary' : 'text.disabled' }}>
              <FolderOutlinedIcon sx={{ fontSize: 17, flexShrink: 0 }} />
              <Typography noWrap sx={{ fontSize: '0.8rem' }}>{s.projectName || 'Unassigned'}</Typography>
            </Box>
            <Box sx={{ width: 90 }}>
              {shell.artifactCountBySession[s.id]
                ? <ArtifactIndicator count={shell.artifactCountBySession[s.id]} />
                : <Typography sx={{ color: 'text.disabled', fontSize: '0.8rem' }}>—</Typography>}
            </Box>
            <Typography sx={{ width: 140, fontSize: '0.8rem', color: 'text.secondary' }}>{relativeTime(s.lastActiveAt ?? s.startedAt)}</Typography>
            <Box sx={{ width: 40, display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton
                className="row-more" size="small"
                onClick={e => { e.stopPropagation(); setMenu({ anchor: e.currentTarget, session: s }) }}
                sx={{ opacity: { xs: 1, md: 0 }, transition: 'opacity 0.15s', color: 'text.secondary' }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        ))}
      </Paper>

      {/* Row ⋮ menu */}
      {/* disableRestoreFocus so the rename field (autoFocus) isn't blurred by the menu returning
          focus to the ⋮ trigger on close — that blur was reverting the rename immediately. */}
      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)} disableRestoreFocus anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem onClick={() => menu && startRename(menu.session)}>
          <ListItemIcon><EditOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { if (menu) void openProjectMenu(menu.anchor, menu.session) }}>
          <ListItemIcon><DriveFileMoveOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Move to project</ListItemText>
          <ChevronRightIcon fontSize="small" sx={{ color: 'text.disabled', ml: 1.5 }} />
        </MenuItem>
        <MenuItem onClick={() => { if (menu) { void shell.setSessionPinned(menu.session.id, !menu.session.pinnedAt); setMenu(null) } }}>
          <ListItemIcon>{menu?.session.pinnedAt ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}</ListItemIcon>
          <ListItemText>{menu?.session.pinnedAt ? 'Unpin chat' : 'Pin chat'}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => menu && copySessionId(menu.session.id)}>
          <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Copy session ID</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { if (menu) setConfirmDelete(menu.session); setMenu(null) }} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteOutlineIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
          <ListItemText>Delete chat</ListItemText>
        </MenuItem>
      </Menu>

      {/* "Move to project" submenu — exactly one project or Unassigned. */}
      <Menu anchorEl={projectMenu?.anchor} open={!!projectMenu} onClose={() => setProjectMenu(null)} PaperProps={{ sx: { maxHeight: 320, minWidth: 220 } }} anchorOrigin={{ vertical: 'top', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem onClick={() => void moveOutOfProject()} disabled={!projectMenu?.session.projectId}>
          <ListItemIcon>{!projectMenu?.session.projectId ? <CheckIcon fontSize="small" /> : <FolderOutlinedIcon fontSize="small" />}</ListItemIcon>
          <ListItemText primaryTypographyProps={{ sx: { fontSize: '0.85rem' } }}>Unassigned</ListItemText>
        </MenuItem>
        <Divider />
        {projects.map(p => (
          <MenuItem key={p.id} onClick={() => void moveToProject(p.id)}>
            <ListItemIcon>{projectMenu?.session.projectId === p.id ? <CheckIcon fontSize="small" /> : <FolderOutlinedIcon fontSize="small" />}</ListItemIcon>
            <ListItemText primaryTypographyProps={{ noWrap: true, sx: { fontSize: '0.85rem' } }}>{p.name}</ListItemText>
          </MenuItem>
        ))}
        {projects.length > 0 && <Divider />}
        <MenuItem onClick={() => void createProjectWithChat()}>
          <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ sx: { fontSize: '0.85rem' } }}>New project…</ListItemText>
        </MenuItem>
      </Menu>

      {/* Confirm-delete dialog */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle sx={SERIF_HEADING}>Delete this chat?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            “{confirmDelete?.intentSummary || 'Untitled chat'}” will be removed. You'll have a few seconds to undo.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmAndDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Undo toast — the commitTimer owns the server delete; this is display + Undo only. */}
      <Snackbar
        open={!!pendingDelete}
        message="Chat deleted"
        action={<Button color="primary" size="small" onClick={undoDelete}>Undo</Button>}
      />
    </Box>
  )
}
